import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Package, Send, Loader2, CheckCircle2, Clock, XCircle, Upload, AlertTriangle, ClipboardList } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { smartCompressImage } from '../lib/imageCompression';
import { trackPageView } from '../lib/analytics';
import { WHATSAPP_NUMBER } from '../lib/brand';

interface SubmissionRow {
  id: number;
  submissionType: 'new' | 'update';
  name: string;
  status: 'pending' | 'approved' | 'rejected';
  adminNotes?: string | null;
  createdAt: number;
  reviewedAt?: number | null;
}

const STATUS_BADGE: Record<string, { label: string; cls: string; icon: typeof Clock }> = {
  pending: { label: '待審核', cls: 'bg-amber-100 text-amber-800', icon: Clock },
  approved: { label: '已上架', cls: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  rejected: { label: '未通過', cls: 'bg-red-100 text-red-700', icon: XCircle },
};

const FALLBACK_PHONE = WHATSAPP_NUMBER;

export default function SupplierSubmitProduct() {
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token') || '');
  const [sessionState, setSessionState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [sessionError, setSessionError] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [submissions, setSubmissions] = useState<SubmissionRow[]>([]);
  const [expiresAt, setExpiresAt] = useState(0);
  const [isStanding, setIsStanding] = useState(false);
  const [portalTab, setPortalTab] = useState<'submit' | 'records'>('submit');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [spicinessLevel, setSpicinessLevel] = useState(1);
  const [suggestedPrice, setSuggestedPrice] = useState('');
  const [suggestedOriginalPrice, setSuggestedOriginalPrice] = useState('');
  const [suggestedStock, setSuggestedStock] = useState('50');
  const [note, setNote] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const mobile = useMemo(() => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent), []);

  useEffect(() => {
    trackPageView(window.location.pathname.startsWith('/supplier/portal') ? '/supplier/portal' : '/supplier/submit-product');
    if (!token) {
      setSessionState('error');
      setSessionError('缺少連結參數');
      return;
    }
    fetch(`/api/public/supplier/session?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => {
        const data = d.data || d;
        if (!data || data.error) throw new Error(data?.error || '連結無效');
        setSupplierName(data.supplierName || '');
        setExpiresAt(Number(data.expiresAt || 0));
        setIsStanding(!!data.isStanding);
        setSubmissions(Array.isArray(data.submissions) ? data.submissions : []);
        setSessionState('ready');
      })
      .catch((e) => {
        setSessionState('error');
        setSessionError(e.message || '連結無效');
      });
  }, [token]);

  const handleImagePick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (images.length >= 3) {
      setMessage({ kind: 'err', text: '最多 3 張圖片' });
      return;
    }
    setUploading(true);
    setMessage(null);
    try {
      const compressed = await smartCompressImage(file).catch(() => null);
      const uploadFile = compressed?.file ?? file;
      const fd = new FormData();
      fd.append('file', uploadFile);
      const res = await fetch(`/api/public/supplier/upload?token=${encodeURIComponent(token)}`, { method: 'POST', body: fd });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || !data?.url) throw new Error(data?.error || '上傳失敗');
      setImages((cur) => [...cur, data.url]);
    } catch (err) {
      setMessage({ kind: 'err', text: err instanceof Error ? err.message : '上傳失敗' });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await fetch('/api/public/supplier/product-submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          name: name.trim(),
          description: description.trim(),
          spicinessLevel,
          suggestedPrice: parseFloat(suggestedPrice),
          suggestedOriginalPrice: parseFloat(suggestedOriginalPrice) || 0,
          suggestedStock: parseInt(suggestedStock, 10) || 0,
          imageUrl: images[0] || '',
          imageUrl2: images[1] || '',
          imageUrl3: images[2] || '',
          note: note.trim(),
        }),
      });
      const d = await res.json();
      const data = d.data || d;
      if (!res.ok || data?.error) throw new Error(data?.error || '提交失敗');

      setName(''); setDescription(''); setSuggestedPrice(''); setSuggestedOriginalPrice('');
      setSuggestedStock('50'); setNote(''); setSpicinessLevel(1); setImages([]);
      setMessage({ kind: 'ok', text: '✅ 提交成功！我哋會盡快審核，結果會經 WhatsApp 通知你。' });
      const refreshed = await fetch(`/api/public/supplier/session?token=${encodeURIComponent(token)}`).then((r) => r.json());
      const rd = refreshed.data || refreshed;
      if (rd?.submissions) setSubmissions(rd.submissions);
    } catch (err) {
      setMessage({ kind: 'err', text: err instanceof Error ? err.message : '提交失敗' });
    } finally {
      setSubmitting(false);
    }
  };

  const waHref = mobile
    ? `https://wa.me/${FALLBACK_PHONE}?text=${encodeURIComponent('你好，我嘅供貨商連結有問題，想重新索取，謝謝！')}`
    : `https://web.whatsapp.com/send?phone=${FALLBACK_PHONE}&text=${encodeURIComponent('你好，我嘅供貨商連結有問題，想重新索取，謝謝！')}`;

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />

      <section className="relative bg-x1493-dark text-white py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">供貨商門戶</h1>
          <p className="text-white/80">
            {sessionState === 'ready' ? `${supplierName}，歡迎！提交產品經本店審批後上架。` : '提交產品資料，經審批後上架'}
          </p>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-3xl mx-auto px-4">
          {sessionState === 'loading' && (
            <div className="text-center py-20 text-stone-500">載入中...</div>
          )}

          {sessionState === 'error' && (
            <div className="text-center py-16 bg-white rounded-2xl border border-stone-200">
              {!token ? (
                <>
                  <Package className="mx-auto text-x1493-primary mb-4" size={48} />
                  <p className="text-stone-800 font-bold text-lg mb-2">供貨商門戶</p>
                  <p className="text-stone-500 text-sm max-w-md mx-auto mb-6">
                    這裡是本店的供貨商入口——提交產品、查看審批進度，全部經你的<strong>專屬連結</strong>進入（毋須帳號密碼）。
                    專屬連結由本店經 WhatsApp 發出，尚未持有連結？歡迎聯絡我們開通合作。
                  </p>
                </>
              ) : (
                <>
                  <AlertTriangle className="mx-auto text-amber-500 mb-4" size={48} />
                  <p className="text-stone-700 font-bold mb-2">{sessionError}</p>
                  <p className="text-stone-500 text-sm mb-6">連結可能已過期（48 小時時限）或已被取消，重新索取一條新的即可。</p>
                </>
              )}
              <a href={waHref} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 bg-[#25D366] text-white font-bold px-6 py-3 rounded-xl hover:bg-[#128C7E] transition">
                WhatsApp 聯絡本店{!token ? '，開通合作' : '，重新索取連結'}
              </a>
            </div>
          )}

          {sessionState === 'ready' && (
            <div className="space-y-8">
              {expiresAt > 0 && (
                <p className="text-xs text-stone-400">
                  {isStanding
                    ? '此為長期連結，可收藏作書籤随时進入。'
                    : `連結有效期至 ${new Date(expiresAt * 1000).toLocaleString('zh-HK')}（已提交嘅項目唔受影響，過期前可多次提交）`}
                </p>
              )}

              {/* Portal tabs */}
              <div className="flex gap-2">
                {[
                  { key: 'submit', label: '提交產品', icon: Package },
                  { key: 'records', label: `我的記錄${submissions.length ? ` (${submissions.length})` : ''}`, icon: ClipboardList },
                ].map((t) => {
                  const TIcon = t.icon;
                  return (
                    <button
                      key={t.key}
                      onClick={() => setPortalTab(t.key as 'submit' | 'records')}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition ${
                        portalTab === t.key
                          ? 'bg-x1493-primary text-white shadow'
                          : 'bg-white text-stone-600 border border-stone-200 hover:border-x1493-primary'
                      }`}
                    >
                      <TIcon size={14} /> {t.label}
                    </button>
                  );
                })}
              </div>

              {/* Submission form */}
              {portalTab === 'submit' && (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4">
                <h2 className="font-bold text-lg text-stone-900 flex items-center gap-2">
                  <Package size={18} className="text-x1493-primary" /> 產品資料
                </h2>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">產品名稱 *</label>
                  <input required value={name} onChange={(e) => setName(e.target.value)}
                         className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-x1493-primary outline-none" placeholder="例如：手工辣椒醬（小辣）" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">產品描述</label>
                  <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)}
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-x1493-primary outline-none" placeholder="成分、味道、賣點等" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">建議售價（HK$）*</label>
                    <input required type="number" min="1" step="0.1" value={suggestedPrice}
                           onChange={(e) => setSuggestedPrice(e.target.value)}
                           className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-x1493-primary outline-none" placeholder="例如 88" />
                    <p className="text-[11px] text-stone-400 mt-1">最終售價由本店審批時確定</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">原價（HK$，選填）</label>
                    <input type="number" min="0" step="0.1" value={suggestedOriginalPrice}
                           onChange={(e) => setSuggestedOriginalPrice(e.target.value)}
                           className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-x1493-primary outline-none" placeholder="例如 108" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">辣度（1–5）</label>
                    <select value={spicinessLevel} onChange={(e) => setSpicinessLevel(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-x1493-primary outline-none bg-white">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">可供庫存</label>
                    <input type="number" min="0" value={suggestedStock}
                           onChange={(e) => setSuggestedStock(e.target.value)}
                           className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-x1493-primary outline-none" />
                  </div>
                </div>

                {/* Images */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">產品圖片（最多 3 張，會自動壓縮）</label>
                  <div className="flex flex-wrap gap-3">
                    {images.map((url, i) => (
                      <div key={url} className="relative w-24 h-24 rounded-lg overflow-hidden border border-stone-200">
                        <img src={url} alt={`產品圖 ${i + 1}`} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => setImages((cur) => cur.filter((u) => u !== url))}
                                className="absolute top-1 right-1 bg-black/50 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">×</button>
                      </div>
                    ))}
                    {images.length < 3 && (
                      <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                              className="w-24 h-24 rounded-lg border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-400 hover:border-x1493-primary hover:text-x1493-primary transition">
                        {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
                        <span className="text-[10px] mt-1">{uploading ? '上傳中' : '加圖片'}</span>
                      </button>
                    )}
                  </div>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImagePick} />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">備註（俾審批人睇）</label>
                  <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-x1493-primary outline-none" placeholder="例如出貨方式、批發價、有效日期等" />
                </div>

                {message && (
                  <div className={`p-3 rounded-lg text-sm ${message.kind === 'ok' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    {message.text}
                  </div>
                )}

                <button type="submit" disabled={submitting || uploading}
                        className="w-full inline-flex items-center justify-center gap-2 bg-x1493-primary text-white font-bold py-3 rounded-xl hover:bg-red-700 transition active:scale-95 disabled:opacity-50">
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  {submitting ? '提交中...' : '提交產品審批'}
                </button>
              </form>
              )}

              {/* My submissions */}
              {portalTab === 'records' && (
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <h2 className="font-bold text-lg text-stone-900 mb-4">我的提交記錄</h2>
                {submissions.length === 0 ? (
                  <p className="text-stone-500 text-sm">未有提交記錄。</p>
                ) : (
                  <ul className="divide-y divide-stone-100">
                    {submissions.map((s) => {
                      const badge = STATUS_BADGE[s.status] || STATUS_BADGE.pending;
                      const Icon = badge.icon;
                      return (
                        <li key={s.id} className="py-3 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-stone-800 truncate">
                              {s.name}
                              {s.submissionType === 'update' && <span className="text-xs text-stone-400 ml-1">（修改）</span>}
                            </p>
                            <p className="text-xs text-stone-400">{new Date(s.createdAt * 1000).toLocaleString('zh-HK')}</p>
                            {s.status === 'rejected' && s.adminNotes && (
                              <p className="text-xs text-red-600 mt-1">原因：{s.adminNotes}</p>
                            )}
                          </div>
                          <span className={`shrink-0 inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${badge.cls}`}>
                            <Icon size={12} /> {badge.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              )}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
